import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { gl } from '~/engine/Engine';
import { RenderStorage } from '~/engine/core/render/RenderStorage';
import { FrameBufferObject } from '~/engine/core/utils/FrameBufferObject';
import {
    RegionOfInterest,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { ViewManager } from '~/engine/view/ViewManager';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { DeformRenderer } from '~/engine/util/algorithms/sdf/DeformRenderer';
import { sdfSurfaceWithinRange } from '~/engine/util/algorithms/sdf/sdfSurfaceProximity';
import {
    createSdfTexture3D,
    uploadSlicesToTexture3D,
} from '~/engine/util/algorithms/sdf/Texture3DUtils';
import {
    clampBoxToGrid,
    intersectWithSliceSlab,
    isEmptyBox,
    VoxelBox,
} from '~/engine/util/algorithms/sdf/voxelBox';
import { StrokeMode } from '~/state/atoms/toolsAtoms';

/**
 * Brush-radius contribution to both falloff lengths: at
 * zero stroke the kernel is a sphere of this radius.
 */
const FALLOFF_PER_BRUSH_RADIUS = 3.0;

/**
 * Along-stroke falloff growth per mm of stroke. Must exceed
 * the bell's steepest slope SLOPE ≈ 1.43 (set by CAP and
 * BASE in DeformRenderer) or the warp folds and pinches off
 * islands; 1.5 sits just above that floor.
 */
const AXIAL_FALLOFF_PER_STROKE_MM = 1.5;

/**
 * Lateral falloff growth per mm of stroke. Unconstrained by
 * the fold criterion; tuned for feel.
 */
const LATERAL_FALLOFF_PER_STROKE_MM = 0.5;

/**
 * Mutable state for an active SDF-native deform stroke.
 *
 * Per frame the warp
 *
 *   sdf_deformed(p) = sdf_original(p - w(p) * v),
 *   v = mouse - mouse_start,
 *   t = sqrt((s/Ta)² + (ρ/Tl)²), s along v, ρ lateral,
 *   Ta = 3 R + 1.5 ||v||,  Tl = 3 R + 0.5 ||v||,
 *   w = bell(t) (see DeformRenderer)
 *
 * is rendered by {@link DeformRenderer} from the pre-stroke
 * snapshot `originalTex` into the live SDF texture, slice
 * by slice over the kernel's Z range.
 *
 * In 2D mode only the deformed view's current slice
 * changes; in 3D mode the full kernel is warped.
 *
 * The CPU pixel cache is untouched during the stroke; on
 * finish the affected slices are read back from the GPU.
 */
export class DeformActionState {
    private readonly structure: RegionOfInterest;
    private readonly view: PlaneView;
    private readonly diameter: number;
    private readonly mode: StrokeMode;
    private readonly textureWrite: TextureWrite;

    /** Texture grid size in voxels (x, y, z). */
    private readonly size: vec3;
    /** Texture grid resolution in mm/voxel. */
    private readonly res: vec3;

    /**
     * CPU snapshot of the SDF at stroke start; used by the
     * surface search and to restore slices on cancel.
     */
    private originalSlices: Uint8Array[] | undefined;

    /**
     * GPU snapshot of the SDF at stroke start; the warp
     * shader's input texture.
     */
    private originalTex: WebGLTexture | undefined;
    /** FBO used to render the warp into the live SDF. */
    private fbo: FrameBufferObject | undefined;

    /** Patient mouse position when the stroke started (mm). */
    private readonly strokeStartMouse: vec3;
    /** Current patient mouse position (mm). */
    private readonly currentMouse: vec3;

    /**
     * Voxel box written during the previous frame; unioned
     * into the next render so voxels leaving the kernel are
     * restored.
     */
    private lastFrameRange: VoxelBox | undefined;

    /** Coalesces mouse events into one warp per animation frame. */
    private rafPending = false;

    /**
     * Z indices of every slice modified during the stroke;
     * resynced on finish, restored on cancel.
     */
    private readonly affectedSlices: Set<number> = new Set();

    public constructor(
        structure: RegionOfInterest,
        view: PlaneView,
        startCoordinates: ReadonlyVec2,
        diameter: number,
        mode: StrokeMode
    ) {
        this.structure = structure;
        this.view = view;
        this.diameter = diameter;
        this.mode = mode;

        const patientPos = view.clientToPatientCoordinates(startCoordinates);
        this.strokeStartMouse = vec3.clone(patientPos);
        this.currentMouse = vec3.clone(patientPos);

        this.textureWrite = structure.startModify('deform');

        this.size = vec3.create();
        this.res = vec3.fromValues(1, 1, 1);

        try {
            const texture = structure.texture;
            if (texture && !texture.isEmpty) {
                const grid = texture.textureGrid;
                vec3.set(this.size, grid.sizeX, grid.sizeY, grid.sizeZ);
                vec3.set(this.res, grid.resX, grid.resY, grid.resZ);

                // The CPU cache is populated by startModify, so
                // reading `pixels` triggers no GPU readback.
                const sdfPixels = texture.signedDistanceField.pixels;
                this.originalSlices = sdfPixels.map((s) => new Uint8Array(s));

                // GPU snapshot used as the warp shader's input;
                // its LINEAR filtering makes the shader's
                // trilinear sample a smooth warped SDF.
                const snapshotTex = createSdfTexture3D(
                    this.size[0],
                    this.size[1],
                    this.size[2]
                );
                if (!snapshotTex) {
                    throw new Error(
                        'Failed to allocate deform snapshot texture'
                    );
                }
                this.originalTex = snapshotTex;
                uploadSlicesToTexture3D(
                    this.originalTex,
                    this.originalSlices,
                    this.size[0],
                    this.size[1]
                );
                this.fbo = new FrameBufferObject(
                    vec2.fromValues(this.size[0], this.size[1])
                );
                this.fbo.unbind();
            }
        } catch (error) {
            // Nothing has been written yet, so cancelling
            // leaves the ROI unchanged and unlocked.
            this.disposeGpuResources();
            this.textureWrite.cancel();
            throw error;
        }
    }

    public get targetStructure(): RegionOfInterest {
        return this.structure;
    }

    public onMouseMove(coordinates: ReadonlyVec2): EventHandlerResult {
        const patientPos = this.view.clientToPatientCoordinates(coordinates);
        vec3.copy(this.currentMouse, patientPos);
        this.updateStrokeStart();
        if (this.rafPending) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        this.rafPending = true;
        requestAnimationFrame(() => {
            if (!this.rafPending) {
                return;
            }
            this.rafPending = false;
            this.applyDeformation();
            // The contour layer is redrawn from the SDF by the repaint this
            // invalidation schedules. Compositing the view here instead would
            // publish it with the layer as it stands, which is the contour
            // before this pass — one frame of wrong geometry, then the correct
            // repaint. One invalidation per pass keeps the frame on the canvas
            // and the SDF in agreement.
            ViewManager.invalidate(
                LayerBit.CONTOUR_LAYER_BIT,
                this.structure.boundingBox
            );
        });
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Commit the deformed SDF: read the affected slices
     * back into the CPU pixel cache, then clip the result
     * to the image volume.
     */
    public finish(): void {
        this.flushPendingFrame();
        const texture = this.structure.texture;
        if (texture && !texture.isEmpty && this.affectedSlices.size > 0) {
            const sdf = texture.signedDistanceField;
            for (const z of this.affectedSlices) {
                sdf.syncSliceFromGpu(z, 2);
            }
            // The warp can drag the structure into the
            // texture's overhang beyond the image volume;
            // discard it on commit.
            texture.cropToImageBounds();
        }
        this.disposeGpuResources();
        this.textureWrite.finish();
    }

    /**
     * Restore the original SDF (Escape): reupload the
     * affected slices to the GPU from the snapshot.
     */
    public cancel(): void {
        this.rafPending = false;
        const texture = this.structure.texture;
        if (
            texture &&
            !texture.isEmpty &&
            this.originalSlices &&
            this.affectedSlices.size > 0
        ) {
            const sdf = texture.signedDistanceField;
            for (const z of this.affectedSlices) {
                sdf.uploadSliceToGpu(this.originalSlices[z], z);
            }
        }
        this.disposeGpuResources();
        this.textureWrite.cancel();
    }

    /**
     * If a frame is pending, run the deformation synchronously
     * so the final state is committed before the stroke ends.
     */
    private flushPendingFrame(): void {
        if (this.rafPending) {
            this.rafPending = false;
            this.applyDeformation();
        }
    }

    private disposeGpuResources(): void {
        if (this.originalTex) {
            gl.deleteTexture(this.originalTex);
            this.originalTex = undefined;
        }
        this.fbo?.delete();
        this.fbo = undefined;
    }

    /**
     * While no point of the segment from stroke start to
     * cursor has the surface within the brush radius, the
     * stroke start follows the cursor; once the path
     * touches the surface again the start freezes and the
     * deform grows from there.
     */
    private updateStrokeStart(): void {
        // The search sphere is the rendered brush sphere.
        const searchRadiusMm = this.diameter / 2;
        const pathMm = vec3.subtract(
            vec3.create(),
            this.currentMouse,
            this.strokeStartMouse
        );
        // Sample the segment densely enough that consecutive
        // spheres overlap.
        const steps = Math.ceil(vec3.length(pathMm) / (searchRadiusMm / 2));
        const candidate = vec3.create();
        for (let i = 0; i <= steps; i++) {
            const f = steps === 0 ? 0 : i / steps;
            vec3.scaleAndAdd(candidate, this.strokeStartMouse, pathMm, f);
            if (this.surfaceWithinRange(candidate, searchRadiusMm)) {
                return;
            }
        }
        vec3.copy(this.strokeStartMouse, this.currentMouse);
    }

    /**
     * Whether the structure surface lies within
     * `searchRadiusMm` of `centerMm`, measured on the
     * pre-stroke SDF snapshot.
     */
    private surfaceWithinRange(
        centerMm: ReadonlyVec3,
        searchRadiusMm: number
    ): boolean {
        const texture = this.structure.texture;
        if (!this.originalSlices || !texture || texture.isEmpty) {
            return false;
        }
        return sdfSurfaceWithinRange(
            this.originalSlices,
            this.size,
            this.res,
            texture.textureGrid.toVoxelSpace(centerMm),
            searchRadiusMm,
            texture.margin
        );
    }

    /**
     * Render this frame's warp into the live SDF texture,
     * restricted to the kernel ellipsoid's bounding box
     * unioned with the previous frame's (so voxels that
     * left the kernel render as identity and are restored).
     */
    private applyDeformation(): void {
        if (!this.originalTex || !this.fbo) {
            return;
        }
        const texture = this.structure.texture;
        if (!texture || texture.isEmpty) {
            return;
        }
        const grid = texture.textureGrid;

        const vMm = vec3.subtract(
            vec3.create(),
            this.currentMouse,
            this.strokeStartMouse
        );
        const strokeMag = vec3.length(vMm);

        if (strokeMag === 0 && !this.lastFrameRange) {
            // Identity warp with nothing written before it:
            // nothing to render.
            return;
        }

        // Falloff lengths of the ellipsoidal kernel.
        const brushFalloffMm = FALLOFF_PER_BRUSH_RADIUS * (this.diameter / 2);
        const axialFalloffMm =
            brushFalloffMm + AXIAL_FALLOFF_PER_STROKE_MM * strokeMag;
        const lateralFalloffMm =
            brushFalloffMm + LATERAL_FALLOFF_PER_STROKE_MM * strokeMag;

        // Half-extent of the kernel ellipsoid's bounding box
        // along world axis i: sqrt(v̂ᵢ²·Ta² + (1 - v̂ᵢ²)·Tl²).
        const vHat =
            strokeMag > 1e-6
                ? vec3.scale(vec3.create(), vMm, 1 / strokeMag)
                : vec3.create();
        const axialSq = axialFalloffMm * axialFalloffMm;
        const lateralSq = lateralFalloffMm * lateralFalloffMm;
        const extentMm = (axisComponent: number): number => {
            const c2 = axisComponent * axisComponent;
            return Math.sqrt(c2 * axialSq + (1 - c2) * lateralSq);
        };

        const mouseVoxel = grid.toVoxelSpace(this.currentMouse);
        const radiusVx = extentMm(vHat[0]) / this.res[0];
        const radiusVy = extentMm(vHat[1]) / this.res[1];
        const radiusVz = extentMm(vHat[2]) / this.res[2];
        const kernelBox: VoxelBox = {
            xMin: Math.floor(mouseVoxel[0] - radiusVx),
            xMax: Math.ceil(mouseVoxel[0] + radiusVx),
            yMin: Math.floor(mouseVoxel[1] - radiusVy),
            yMax: Math.ceil(mouseVoxel[1] + radiusVy),
            zMin: Math.floor(mouseVoxel[2] - radiusVz),
            zMax: Math.ceil(mouseVoxel[2] + radiusVz),
        };
        const current = clampBoxToGrid(
            this.mode === '2d'
                ? intersectWithSliceSlab(
                      kernelBox,
                      mouseVoxel,
                      this.view.viewDirection,
                      grid
                  )
                : kernelBox,
            this.size
        );

        // Union with the previous frame's box.
        const last = this.lastFrameRange;
        const box: VoxelBox = last
            ? {
                  xMin: Math.min(last.xMin, current.xMin),
                  xMax: Math.max(last.xMax, current.xMax),
                  yMin: Math.min(last.yMin, current.yMin),
                  yMax: Math.max(last.yMax, current.yMax),
                  zMin: Math.min(last.zMin, current.zMin),
                  zMax: Math.max(last.zMax, current.zMax),
              }
            : current;

        if (isEmptyBox(box)) {
            return;
        }

        const sdf = texture.signedDistanceField;
        const renderer = RenderStorage.getRenderer(DeformRenderer);
        renderer.renderRange(
            this.originalTex,
            sdf.texture,
            this.fbo,
            this.size,
            grid.voxelToWorld,
            grid.worldToVoxel,
            this.currentMouse,
            vMm,
            axialFalloffMm,
            lateralFalloffMm,
            this.mode,
            this.view.viewDirection,
            grid.voxelExtentAlong(this.view.viewDirection),
            box.xMin,
            box.xMax,
            box.yMin,
            box.yMax,
            box.zMin,
            box.zMax
        );

        for (let z = box.zMin; z <= box.zMax; z++) {
            this.affectedSlices.add(z);
        }

        this.lastFrameRange = current;
    }
}

/**
 * Manages the lifecycle of an SDF-native deform stroke.
 */
export class DeformAction {
    private readonly structure: RegionOfInterest;
    private readonly diameter: number;
    private readonly mode: StrokeMode;

    public constructor(
        structure: RegionOfInterest,
        diameter: number,
        mode: StrokeMode
    ) {
        this.structure = structure;
        this.diameter = diameter;
        this.mode = mode;
    }

    public startAction(
        view: PlaneView,
        coordinates: ReadonlyVec2
    ): DeformActionState {
        return new DeformActionState(
            this.structure,
            view,
            coordinates,
            this.diameter,
            this.mode
        );
    }

    public stopAction(state: DeformActionState): EventHandlerResult {
        state.finish();
        const bb = state.targetStructure.boundingBox;
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            { layers: LayerBit.CONTOUR_LAYER_BIT, volume: bb }
        );
    }
}
